import { Component, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EmployeeValidationService, Employee } from '../../services/employee-validation.service';
import { EmployeeUploadService } from '../../services/employee-upload.service';
import { EmployeeImportRow } from '../../models/employee-import-row.model';
import { AggregatedError, UploadLog, UploadRowSummary } from '../../models/upload-log.model';
import * as XLSX from 'xlsx';
import { lastValueFrom, take } from 'rxjs';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-employee-import',
  standalone: true,
imports: [CommonModule, RouterModule],
  templateUrl: './employee-import.component.html',
  styleUrls: ['./employee-import.component.scss'],
})
export class EmployeeImportComponent {
  selectedFile: File | null = null;
  rows: EmployeeImportRow[] = [];
  logs: UploadLog[] = [];
  validationLogs: string[] = [];
  aggregatedErrors: AggregatedError[] = [];
  activeLog: UploadLog | null = null;

  isLoading = false;
  errorMessage: string | null = null;

  constructor(
    private validation: EmployeeValidationService,
    private uploadService: EmployeeUploadService,
    private cdr: ChangeDetectorRef,
  ) {}

  get failedRows(): UploadRowSummary[] {
    if (!this.activeLog?.summary) return [];
    return this.activeLog.summary.filter((r) => r.status === 'fail' || r.status === 'partial');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    this.reset();
    this.selectedFile = input.files[0];
    input.value = '';
  }

  handleDrop(event: DragEvent): void {
    event.preventDefault();
    if (!event.dataTransfer?.files?.length) return;

    this.reset();
    this.selectedFile = event.dataTransfer.files[0];
  }

  removeFile(): void {
    this.reset();
  }

  private reset(): void {
    this.selectedFile = null;
    this.rows = [];
    this.validationLogs = [];
    this.errorMessage = null;
    this.aggregatedErrors = [];
    this.activeLog = null;
  }

  private normalizeDate(value: any): string {
    if (!value) return '';

    if (typeof value === 'number') {
      const date = XLSX.SSF.parse_date_code(value);
      return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
    }

    return String(value).split('T')[0];
  }

  async upload(): Promise<void> {
    if (!this.selectedFile) return;

    this.isLoading = true;
    const startTime = performance.now();
    this.validationLogs = ['Upload started'];
    this.cdr.detectChanges();

    try {
      const raw = await this.parseFile(this.selectedFile);

      this.rows = raw.map((row, i) => ({
        ...row,
        rowIndex: i + 2,
        errors: [],
        warnings: [],
      }));

      this.validationLogs.push(`Parsed ${this.rows.length} rows`);
      this.cdr.detectChanges();

      const filters = await lastValueFrom(this.validation.getFilterValues().pipe(take(1)));

      for (const row of this.rows) {
        this.validateSingleRow(row, filters);
      }

      const existingUsers = await lastValueFrom(this.validation.getEmployees().pipe(take(1)));
      const existingByEmpNum = new Map<string, Employee>(
        existingUsers
          .filter((u) => !!u.employeeNumber)
          .map((u) => [String(u.employeeNumber), u]),
      );

      const batchEmployeeNumbers = new Set(
        this.rows.map((r) => String(r.EmployeeNumber)).filter(Boolean),
      );

      for (const row of this.rows) {
        if (!row.SupervisorNumber) continue;

        const supervisorNumber = String(row.SupervisorNumber).trim();
        if (
          !batchEmployeeNumbers.has(supervisorNumber) &&
          !existingByEmpNum.has(supervisorNumber)
        ) {
          row.errors!.push(`SupervisorNumber not found: ${supervisorNumber}`);
        }
      }

      const rowsToUpload: EmployeeImportRow[] = [];
      const duplicateEmployeeNumbers: string[] = [];

      for (const row of this.rows) {
        if (row.errors!.length) continue;

        const existing = existingByEmpNum.get(String(row.EmployeeNumber));
        if (!existing) {
          rowsToUpload.push(row);
          continue;
        }

        if (this.isExactDuplicate(row, existing)) {
          row.errors!.push('Duplicate employee – skipped');
          duplicateEmployeeNumbers.push(String(row.EmployeeNumber));
        } else {
          rowsToUpload.push(row);
        }
      }

      if (duplicateEmployeeNumbers.length) {
        this.aggregatedErrors = this.aggregatedErrors.filter(
          (e) => e.field !== 'Duplicate Employees',
        );
        this.aggregatedErrors.push({
          field: 'Duplicate Employees',
          values: duplicateEmployeeNumbers,
        });
      }

      if (rowsToUpload.length) {
        await lastValueFrom(this.uploadService.uploadRows(rowsToUpload).pipe(take(1)));
        this.validationLogs.push(`Uploaded ${rowsToUpload.length} employees`);
        this.cdr.detectChanges();
      }

      const user = await lastValueFrom(this.uploadService.getCurrentUser().pipe(take(1)));
      const uploadedBy = `${user.lastName}, ${user.firstName} (${user.id})`;

      const summary: UploadRowSummary[] = this.rows.map((r) => {
        const hasErrors = r.errors!.length > 0;
        const hasWarnings = r.warnings!.length > 0;

        return {
          rowIndex: r.rowIndex!,
          employeeNumber: r.EmployeeNumber,
          status: hasErrors ? 'fail' : hasWarnings ? 'partial' : 'success',
          errors: r.errors ?? [],
          warnings: r.warnings ?? [],
        };
      });

      const newLog: UploadLog = {
        uploadedBy,
        timestamp: new Date(),
        durationMs: Math.round(performance.now() - startTime),
        totalRows: this.rows.length,
        successCount: summary.filter((s) => s.status === 'success').length,
        errors: summary.flatMap((s) =>
          s.errors.map((e) => ({ rowIndex: s.rowIndex, message: e })),
        ),
        warnings: summary.flatMap((s) =>
          s.warnings.map((w) => ({ rowIndex: s.rowIndex, message: w })),
        ),
        summary,
      };

      this.logs = [newLog, ...this.logs];
      this.activeLog = newLog;
      this.cdr.detectChanges();
    } catch (err: any) {
      console.error(err);
      this.errorMessage = err?.message ?? 'Upload failed';
      this.validationLogs.push(`ERROR: ${this.errorMessage}`);
      this.cdr.detectChanges();
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private parseFile(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array((e.target as any).result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          resolve(XLSX.utils.sheet_to_json(sheet, { defval: null }));
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  private validateSingleRow(row: EmployeeImportRow, lists: any): void {
    if (!row.EmployeeNumber) row.errors!.push('EmployeeNumber required');
    if (!row.FirstName) row.errors!.push('FirstName required');
    if (!row.LastName) row.errors!.push('LastName required');
    if (!row.HireDate) row.errors!.push('HireDate required');
    if (!row.EmploymentStatus) row.errors!.push('EmploymentStatus required');
  }

  private isExactDuplicate(row: EmployeeImportRow, existing: Employee): boolean {
    return (
      String(row.FirstName) === String(existing.firstName) &&
      String(row.LastName) === String(existing.lastName)
    );
  }
}